import React from 'react';
import { Home, Calendar, User, Ticket, ShoppingBag } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// 簡單的 SVG Icons
const Icons = {
  Home: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
  ),
  Calendar: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
  ),
  // 新增：優惠券 Icon
  Ticket: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
  ),
  User: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
  ),
  Login: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
  )
};

const MerchantNavigation = ({ merchantSlug }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  console.log("Nav State:", {
    isAuthenticated,
    userName: user?.default_display_name || user?.display_name,
    mySlug: merchantSlug
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/${merchantSlug}/login`);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const displayName = user?.default_display_name || user?.display_name || '會員';
  const avatarUrl = user?.avatar_url || user?.line_profile?.picture;

  // === Desktop View ===
  const DesktopNav = () => (
    <div className="hidden md:flex items-center space-x-4">
      {isAuthenticated && user ? (
        <>
          <Link to={`/${merchantSlug}/coupons`} className={`text-sm font-medium ${isActive(`/${merchantSlug}/coupons`) ? 'text-blue-600' : 'text-gray-600'}`}>
            我的優惠
          </Link>
          <Link to={`/${merchantSlug}/booking-history`} className={`text-sm font-medium ${isActive(`/${merchantSlug}/booking-history`) ? 'text-blue-600' : 'text-gray-600'}`}>
            體驗記錄
          </Link>
          <Link to={`/${merchantSlug}/profile`} className={`text-sm font-medium ${isActive(`/${merchantSlug}/profile`) ? 'text-blue-600' : 'text-gray-600'}`}>
            個人資料
          </Link>
          <div className="flex items-center space-x-2 pl-2 border-l border-gray-300">
            {avatarUrl && (
              <img src={avatarUrl} alt="Avatar" className="w-6 h-6 rounded-full" />
            )}
            <span className="text-sm text-gray-700">{displayName}</span>
          </div>
          <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-red-600">登出</button>
        </>
      ) : (
        <Link to={`/${merchantSlug}/login`} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md">
          登入
        </Link>
      )}
    </div>
  );

  // === Mobile View ===
  const MobileBottomNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe-area z-50">
      <div className="flex justify-around items-center h-16">

        {/*<Link to={`/${merchantSlug}`} className={`flex flex-col items-center justify-center w-full h-full ${isActive(`/${merchantSlug}`) ? 'text-blue-600' : 'text-gray-500'}`}>
          <Icons.Home />
          <span className="text-[10px] mt-1">首頁</span>
        </Link>*/}

        {isAuthenticated ? (
          <>
            {/* 中間位置：替換為優惠券 */}
            <Link to={`/${merchantSlug}/coupons`} className={`flex flex-col items-center justify-center w-full h-full ${isActive(`/${merchantSlug}/coupons`) ? 'text-blue-600' : 'text-gray-500'}`}>
              <Icons.Ticket />
              <span className="text-[10px] mt-1">優惠</span>
            </Link>

            <Link to={`/${merchantSlug}/booking-history`} className={`flex flex-col items-center justify-center w-full h-full ${isActive(`/${merchantSlug}/booking-history`) ? 'text-blue-600' : 'text-gray-500'}`}>
              <Icons.Calendar />
              <span className="text-[10px] mt-1">記錄</span>
            </Link>

            <Link to={`/${merchantSlug}/profile`} className={`flex flex-col items-center justify-center w-full h-full ${isActive(`/${merchantSlug}/profile`) ? 'text-blue-600' : 'text-gray-500'}`}>
              <div className="relative">
                {avatarUrl ? (
                  <img src={avatarUrl} className={`w-6 h-6 rounded-full border ${isActive(`/${merchantSlug}/profile`) ? 'border-blue-600' : 'border-transparent'}`} alt="Me" />
                ) : (
                  <Icons.User />
                )}
              </div>
              <span className="text-[10px] mt-1">我的</span>
            </Link>
          </>
        ) : (
          <Link to={`/${merchantSlug}/login`} className={`flex flex-col items-center justify-center w-full h-full ${isActive(`/${merchantSlug}/login`) ? 'text-blue-600' : 'text-gray-500'}`}>
            <Icons.Login />
            <span className="text-[10px] mt-1">登入</span>
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <>
      <DesktopNav />
      <MobileBottomNav />
    </>
  );
};

export default MerchantNavigation;